import {Box, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import type {TFunction} from "i18next";
import React from "react";
import {SigningAmountRow, SigningSummary, signingSubtitle, signingTitle} from "./SigningLines";
import {GrandTotalRow, useRowTone} from "./SigningRows";
import SigningShell, {variantOf} from "./SigningShell";
import {SigningFormat} from "./format.util";
import {DigitalSigningSnapshot} from "./digitalSigning.types";

interface Props {
    snapshot: DigitalSigningSnapshot
    format: SigningFormat
    t: TFunction
    language: string
    tip?: React.ReactNode
    grandTotalCents: number
    signature: React.ReactNode
    consent: string
    action?: React.ReactNode
    footer?: React.ReactNode
    fill?: boolean
}

function SigningSubtitle({text, align}: {text: string, align: 'left' | 'center'}) {
    const tone = useRowTone();

    return (
        <Typography variant={'body1'} sx={{mt: {xs: 1, sm: 1.25}, fontSize: {xs: 17, sm: 18}, lineHeight: 1.4, color: tone.label, textAlign: align, textWrap: 'balance'}}>
            {text}
        </Typography>
    );
}

/** Standard layout for both the guest signing form and the signed view. */
function SigningLayout({snapshot, format, t, language, tip, grandTotalCents, signature, consent, action, footer, fill}: Props) {
    const band = variantOf(snapshot.theme) === 'HERO';
    // Hero shows the amount in the card, next to the tip
    const amountInCard = band;
    const align = band ? 'left' : 'center';
    const subtitle = signingSubtitle(snapshot, language);

    const head = (
        <Box>
            {/* Large, in the display face */}
            <Typography variant={'h1'} sx={{textAlign: align, textWrap: 'balance', color: 'inherit'}}>
                {signingTitle(snapshot, t, language)}
            </Typography>
            {!!subtitle && <SigningSubtitle text={subtitle} align={align}/>}
            <Box sx={{mt: {xs: 3, sm: band ? 3 : 4}}}>
                <SigningSummary snapshot={snapshot} format={format} t={t} withAmount={!amountInCard}/>
            </Box>
        </Box>
    );

    const body = (
        <Stack gap={band ? {xs: 3.5, sm: 3} : {xs: 4, sm: 5}}>
            {amountInCard && (
                <Box component={'dl'} sx={{m: 0, mt: -1.25}}>
                    <SigningAmountRow snapshot={snapshot} format={format} t={t}/>
                </Box>
            )}

            {!!tip && (
                <Stack gap={band ? 2.5 : {xs: 3, sm: 3.5}}>
                    {tip}
                    <GrandTotalRow label={t('Total amount')} value={format.money(grandTotalCents)}/>
                </Stack>
            )}

            <Box>
                {signature}
                <Typography variant={'body2'} color={'text.secondary'} sx={{pt: 2, pb: 1.25, textWrap: 'pretty'}}>
                    {consent}
                </Typography>
            </Box>

            {action}
        </Stack>
    );

    return (
        <SigningShell
            theme={snapshot.theme}
            hotel={snapshot.hotel}
            head={head}
            body={body}
            footer={footer}
            fill={fill}
        />
    );
}

/** Resolves the consent sentence for the specified language, snapshot, or default fallback. */
export const consentTextOf = (snapshot: DigitalSigningSnapshot, t: TFunction, language?: string): string => (
    (language ? snapshot.consentTexts?.[language]?.trim() : undefined)
    || snapshot.consentText?.trim()
    || t('By signing, I confirm the services and the total above.')
);

export default SigningLayout;
